import { Fragment, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { connect, useDispatch, useSelector } from 'react-redux'
import { ProgressBar } from 'primereact/progressbar'
import {
    Analytics,
    AppState,
    layoutActions,
    LayoutActivityType,
} from '@Techedge/digitalportal-shared'
import Header from '../components/Layout/Header/Header'
import Sidebar from '../components/Layout/Sidebar/SideBar'
import MainContent from '../components/Layout/MainContent/MainContent'
import NewActivityNews from '../components/ActivityCreation/News/NewActivityNews'
import { NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE } from '../redux/reducers/activity-creation'
import { Color, LuxButton, LuxCustomBreadcrumb } from '@lux/wardrobe-lib'
import NewActivityTask from '../components/ActivityCreation/Task/NewActivityTask'
import ActivityType from '../components/ActivityCreation/ActivityType/ActivityType'
import StepBar from '../components/ActivityCreation/StepBar/StepBar'
import {
    NewActivitySectionType,
    NewActivityStepLabelMap,
} from '../models/new-activity-section-type'
import { getDepartments, getFiltersDomain, getRecipients } from '../redux/actions/filters'
import {
    getProjectsActivityTypes,
    resetAllForm,
    setActivityStatus,
    setCtaTemplate,
    setCurrentStep,
    setIsActivityTypeStep,
    setSelectedActivityType,
} from '../redux/actions/activity-creation'
import { creationAnalyticsPageTypeMap } from '../models/analyticsPageTypeMap'
import { ActivityStep } from '../models/new-activity-creation-state'
import NewActivityVisit from '../components/ActivityCreation/Visit/NewActivityVisit'
import './NewActivity.scss'
import {
    selectCurrentStep,
    selectIsActivityTypeStep,
    selectSelectedActivityType,
} from '../redux/selector/activity-creation'
import { selectProjectDetails } from '../redux/selector/activity-execution'
import { SectionFilter } from '../models/search'

const NewActivity = (props) => {
    const {
        closeNewActivity,
        activeActivity,
        newsActivity,
        taskActivity,
        surveyTaskActivity,
        ctaTemplate,
        getDepartments,
        getRecipients,
        getFiltersDomain,
        setActivityStatus,
        isEdit,
        isClone,
        visitActivity,
        hideFooter,
    } = props

    const intl = useIntl()
    const dispatch = useDispatch()
    const isActivityTypeStep = useSelector(selectIsActivityTypeStep)
    const selectedActivityType = useSelector(selectSelectedActivityType)
    const projectDetails = useSelector(selectProjectDetails)
    const currentStep = useSelector(selectCurrentStep)
    const hasSocialHubEnabled = useSelector(
        (state: AppState) => state.userReducer.user.phase3_enabled
    )

    const componentClass = 'smart-modals-new-activity'

    const [stepList, setStepList] = useState<ActivityStep[]>([])
    const [progressBarValue, setProgressBarValue] = useState<number>(0)
    const [currentStepNumber, setCurrentStepNumber] = useState<number>(1)
    const [totalStep, setTotalStep] = useState<number>(0)

    const activityBreadcrumb = {
        id: '0',
        label: intl.formatMessage({
            id: 'modals.new.activity.breadcrumbs.activity',
            defaultMessage: 'Activity',
        }),
        className: 'activity selectable force-opacity-1',
        disabled: true,
    }

    const createBreadcrumb = {
        id: '1',
        label: intl.formatMessage({
            id: 'modals.new.activity.breadcrumbs.create',
            defaultMessage: 'Create',
        }),
        disabled: true,
    }

    useEffect(() => {
        dispatch(
            setCtaTemplate(
                <>
                    <div></div>
                    <div>
                        <LuxButton
                            text={intl.formatMessage({
                                id: 'next',
                                defaultMessage: 'Next',
                            })}
                            color={Color.BLACK}
                            onClick={() =>
                                dispatch(
                                    setActivityStatus(
                                        selectedActivityType.key,
                                        !selectedActivityType.active,
                                        true
                                    )
                                )
                            }
                        />
                    </div>
                </>
            )
        )
    }, [selectedActivityType, isActivityTypeStep])

    useEffect(() => {
        if (!projectDetails || !projectDetails._id) {
            dispatch(setSelectedActivityType({ key: LayoutActivityType.TASK }))
            dispatch(setIsActivityTypeStep(true))
            dispatch(setCurrentStep(NewActivitySectionType.ACTIVITY_TYPE))
            Analytics._sendAnalyticsLinkEvent('Activities', 'CreationPage', 'Static')
        }
        if (projectDetails && projectDetails._id && isEdit) {
            Analytics._sendAnalyticsLinkEvent(
                'Activities',
                creationAnalyticsPageTypeMap[projectDetails.projectType][projectDetails.module]
                    .edit,
                'Static'
            )
        }
        getDepartments()
        getRecipients()
        getFiltersDomain()
        getProjectsActivityTypes({ dispatch, projectDetails, isEdit, isClone })
    }, [])

    useEffect(() => {
        const activityType = isEdit ? projectDetails?.projectType : activeActivity
        let newStepList = []
        const isEditResult =
            isEdit &&
            projectDetails?.status !== SectionFilter.draft &&
            projectDetails?.status !== SectionFilter.rejected
        switch (activityType) {
            case LayoutActivityType.NEWS:
                newStepList = [
                    {
                        label: 'modals.new.activity.section.activity.type',
                        isDone: true,
                        key: NewActivitySectionType.ACTIVITY_TYPE,
                    },
                    newsActivity.sectionMap[NewActivitySectionType.NEWS_TYPE],
                    newsActivity.sectionMap[NewActivitySectionType.MAIN_INFO],
                    newsActivity.sectionMap[NewActivitySectionType.DISTRIBUTION],
                    newsActivity.sectionMap[NewActivitySectionType.SCHEDULING],
                ]
                if (isEditResult)
                    newStepList.splice(
                        1,
                        0,
                        newsActivity.sectionMap[NewActivitySectionType.EDITING_REASON]
                    )
                if ((!isEdit && hasSocialHubEnabled) || (isEdit && !!projectDetails.badge)) {
                    newStepList.splice(
                        4 + (isEditResult ? 1 : 0),
                        0,
                        newsActivity.sectionMap[NewActivitySectionType.POINTS_ASSIGNMENT]
                    )
                }
                setStepList(newStepList)
                return
            case LayoutActivityType.TASK:
                newStepList = [
                    {
                        label: 'modals.new.activity.section.activity.type',
                        isDone: true,
                        key: NewActivitySectionType.ACTIVITY_TYPE,
                    },
                    taskActivity.sectionMap[NewActivitySectionType.MAIN_INFO],
                    taskActivity.sectionMap[NewActivitySectionType.ACTION],
                    taskActivity.sectionMap[NewActivitySectionType.DISTRIBUTION],
                    taskActivity.sectionMap[NewActivitySectionType.SCHEDULING],
                ]
                if (isEditResult)
                    newStepList.splice(
                        1,
                        0,
                        taskActivity.sectionMap[NewActivitySectionType.EDITING_REASON]
                    )
                if ((!isEdit && hasSocialHubEnabled) || (isEdit && !!projectDetails.badge)) {
                    newStepList.splice(
                        4 + (isEditResult ? 1 : 0),
                        0,
                        taskActivity.sectionMap[NewActivitySectionType.POINTS_ASSIGNMENT]
                    )
                }
                setStepList(newStepList.filter((step) => !step.isDisabled && !step.isHide))
                return
            case LayoutActivityType.SURVEY_TASK:
                newStepList = [
                    {
                        label: 'modals.new.activity.section.activity.type',
                        isDone: true,
                        key: NewActivitySectionType.ACTIVITY_TYPE,
                    },
                    surveyTaskActivity.sectionMap[NewActivitySectionType.MAIN_INFO],
                    surveyTaskActivity.sectionMap[NewActivitySectionType.ACTION],
                    surveyTaskActivity.sectionMap[NewActivitySectionType.DISTRIBUTION],
                    surveyTaskActivity.sectionMap[NewActivitySectionType.SCHEDULING],
                ]
                if (isEditResult)
                    newStepList.splice(
                        1,
                        0,
                        surveyTaskActivity.sectionMap[NewActivitySectionType.EDITING_REASON]
                    )
                if ((!isEdit && hasSocialHubEnabled) || (isEdit && !!projectDetails.badge)) {
                    newStepList.splice(
                        4 + (isEditResult ? 1 : 0),
                        0,
                        surveyTaskActivity.sectionMap[NewActivitySectionType.POINTS_ASSIGNMENT]
                    )
                }
                setStepList(newStepList.filter((step) => !step.isDisabled && !step.isHide))
                return
            case LayoutActivityType.VISIT:
                newStepList = [
                    {
                        label: 'modals.new.activity.section.activity.type',
                        isDone: true,
                        key: NewActivitySectionType.ACTIVITY_TYPE,
                    },
                    visitActivity.sectionMap[NewActivitySectionType.MAIN_INFO],
                    visitActivity.sectionMap[NewActivitySectionType.ACTION],
                    visitActivity.sectionMap[NewActivitySectionType.SCHEDULING],
                ]
                if (isEditResult)
                    newStepList.splice(
                        1,
                        0,
                        visitActivity.sectionMap[NewActivitySectionType.EDITING_REASON]
                    )
                if ((!isEdit && hasSocialHubEnabled) || (isEdit && !!projectDetails.badge)) {
                    newStepList.splice(
                        3 + (isEditResult ? 1 : 0),
                        0,
                        visitActivity.sectionMap[NewActivitySectionType.POINTS_ASSIGNMENT]
                    )
                }
                setStepList(newStepList.filter((step) => !step.isDisabled && !step.isHide))
                return
            default:
                setStepList([])
        }
    }, [
        activeActivity,
        taskActivity.sectionMap,
        newsActivity.sectionMap,
        visitActivity.sectionMap,
        surveyTaskActivity.sectionMap,
    ])

    useEffect(() => {
        let stepIndex = stepList.findIndex((s) => s.key === currentStep)
        setProgressBarValue(((stepIndex + 1) / stepList.length) * 100)
        setCurrentStepNumber(stepIndex + 1)
        setTotalStep(stepList.length)
    }, [currentStep, stepList])

    const getSections = () => {
        const activityType = isEdit ? projectDetails?.projectType : activeActivity
        switch (activityType) {
            case LayoutActivityType.NEWS:
                return <NewActivityNews />
            case LayoutActivityType.TASK:
                return <NewActivityTask />
            case LayoutActivityType.VISIT:
                return <NewActivityVisit />
            default:
                ;<Fragment></Fragment>
        }
    }

    const onClose = () => {
        closeNewActivity()
    }

    const isActivityLoaded = () => {
        return !isEdit || (isEdit && !!projectDetails?.projectType)
    }

    return (
        <div className="smart-modals-fullscreen">
            <div className={componentClass} data-isfullscreen>
                <div className={`${componentClass}__header`}>
                    <Header onClose={onClose} isFullScreen>
                        <LuxCustomBreadcrumb pathArray={[activityBreadcrumb, createBreadcrumb]} />
                    </Header>
                </div>
                <div className={`${componentClass}__header-mobile`}>
                    <div className={`${componentClass}__header-mobile__navbar`}>
                        <div className={`${componentClass}__header-mobile__navbar--title`}>
                            {intl.formatMessage({
                                id: 'modals.new.activity.news.create',
                                defaultMessage: 'Create',
                            })}
                        </div>
                        <div
                            className={`${componentClass}__header-mobile__navbar--icon`}
                            onClick={onClose}>
                            <i className="cu cu-times" />
                        </div>
                    </div>
                    <div className={`${componentClass}__header-mobile__stepbar`}>
                        <div className={`${componentClass}__header-mobile__stepbar--step`}>
                            {`${intl.formatMessage({
                                id: 'modals.new.activity.step.bar.mobile',
                                defaultMessage: 'Step',
                            })} ${currentStepNumber} ${intl.formatMessage({
                                id: 'modals.new.activity.step.bar.of',
                                defaultMessage: 'of',
                            })} ${totalStep}: ${
                                currentStep &&
                                intl.formatMessage({
                                    id: NewActivityStepLabelMap[currentStep],
                                    defaultMessage: currentStep,
                                })
                            }`}
                        </div>
                    </div>
                    <ProgressBar value={progressBarValue} />
                </div>
                <div className={`${componentClass}__main`}>
                    <div className={`${componentClass}__sidebar`}>
                        <Sidebar>{isActivityLoaded() && <StepBar stepList={stepList} />}</Sidebar>
                    </div>
                    <div className={`${componentClass}__content`}>
                        {isActivityLoaded() && (
                            <MainContent footer={ctaTemplate} hideFooter={hideFooter}>
                                <div className={`${componentClass}__title`}>
                                    {intl.formatMessage({
                                        id: 'modals.new.activity.title',
                                        defaultMessage: 'Create activity',
                                    })}
                                </div>
                                {isActivityTypeStep && <ActivityType />}
                                {!isActivityTypeStep && getSections()}
                            </MainContent>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    activeActivity: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].activeActivity,
    newsActivity: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].newsActivity,
    taskActivity: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].taskActivity,
    surveyTaskActivity: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].surveyTaskActivity,
    visitActivity: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].visitActivity,
    ctaConfig: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].ctaConfig,
    ctaTemplate: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].ctaTemplate,
    isEdit: state.layoutReducer.modals.props?.isEdit,
    isClone: state.layoutReducer.modals.props?.isClone,
    hideFooter: state.layoutReducer.modals.props?.hideFooter,
})

const mapDispatchToProps = (dispatch: any) => ({
    closeNewActivity: () => {
        dispatch(resetAllForm())
        dispatch(
            layoutActions.setModals({
                type: null,
                props: {
                    id: null,
                    isEdit: false,
                    isClone: false,
                    visit_id: null,
                    survey_id: null,
                    grsdToken: null,
                    grsdParams: null,
                },
            })
        )
    },
    setLoading: (loading) => dispatch(layoutActions.setModals({ props: { loading } })),
    getDepartments: () => {
        dispatch(getDepartments())
    },
    getRecipients: () => {
        dispatch(getRecipients())
    },
    getFiltersDomain: () => {
        dispatch(getFiltersDomain())
    },
    setActivityStatus: (key: LayoutActivityType, value: boolean, disableReset: boolean) =>
        dispatch(setActivityStatus(key, value, disableReset)),
    resetAllForm: () => dispatch(resetAllForm()),
})

export default connect(mapStateToProps, mapDispatchToProps)(NewActivity)
